/**
 * "Duplicate this prompt" dialog, launched from the prompt-detail page's
 * header. Collects a new key/name/org/report types, then creates a draft Prompt row
 * seeded with the source prompt's content and drops the user onto its
 * evaluations page — the same flow "Start a new prompt" ends on, just
 * pre-filled instead of blank. Exists so duplicating a prompt doesn't
 * require asking Rio to do it in chat.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog } from "@fronterahealth/frontera-ui-components";

import type { DuplicatePromptInput, ReportTypeEnum } from "@api/rest/types-and-hooks";
import { useBasecampOrganizationsRetrieve, useDuplicatePrompt, useListPrompts } from "@api/rest/types-and-hooks";
import { OrgOwnerSelect } from "@components/Prompts/OrgOwnerSelect";
import { ReportTypeCheckboxes } from "@components/Prompts/ReportTypeTags";
import { GLOBAL_ORG_SENTINEL } from "@components/Prompts/constants";
import { FormInput } from "@components/forms/FormInput";
import { FormContainer } from "@components/forms/FormLayout";
import { useFormUtils } from "@components/forms/useFormUtils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

// Mirrors NewPrompt.tsx's KEY_PATTERN/RESERVED_KEYS — same constraints
// apply to a duplicate's new key as to any other prompt key.
const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{1,79}$/;
const RESERVED_KEYS = new Set(["new"]);

interface DuplicatePromptDialogProps {
  isDialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  sourceKey: string;
  sourceName: string;
  sourceOrganizationId?: string | null;
  sourceReportTypes: ReportTypeEnum[];
}

export const DuplicatePromptDialog: React.FC<DuplicatePromptDialogProps> = ({
  isDialogOpen,
  setDialogOpen,
  sourceKey,
  sourceName,
  sourceOrganizationId,
  sourceReportTypes,
}) => {
  const navigate = useNavigate();
  const duplicateMutation = useDuplicatePrompt();
  const { data: orgsResponse } = useBasecampOrganizationsRetrieve({}, { query: { enabled: isDialogOpen } });

  const orgs = useMemo(
    () =>
      orgsResponse?.status === 200 ? (Array.isArray(orgsResponse.data) ? orgsResponse.data : [orgsResponse.data]) : [],
    [orgsResponse],
  );

  const [debouncedKey, setDebouncedKey] = useState("");

  // Guards against a request that's still in flight when the user hits
  // Cancel: without this, a late-resolving success would still close
  // the (already-dismissed) dialog and navigate the user away.
  const cancelledRef = useRef(false);

  const { formState, onSubmit, watch, setValue, register, reset } = useFormUtils<DuplicatePromptInput>({
    mutationFn: async (params) => {
      cancelledRef.current = false;
      const newKey = params.new_key ?? "";
      if (!KEY_PATTERN.test(newKey)) {
        notifyError(
          "Prompt key must start with a letter and contain only letters, digits, and underscores (2-80 chars).",
        );
        return;
      }
      if (RESERVED_KEYS.has(newKey)) {
        notifyError(`'${newKey}' is a reserved route segment — the prompt would be unreachable. Pick a different key.`);
        return;
      }
      if (!params.report_types?.length) {
        notifyError("Select at least one report type this prompt is valid for.");
        return;
      }

      const formOrgId = params.organization_id;
      const organization_id = !formOrgId || formOrgId === GLOBAL_ORG_SENTINEL ? null : formOrgId;

      try {
        const result = await duplicateMutation.mutateAsync({
          key: sourceKey,
          data: {
            new_key: newKey,
            new_name: params.new_name || undefined,
            organization_id,
            report_types: params.report_types,
          },
        });

        if (cancelledRef.current) return;

        if (result.status === 201) {
          notifySuccess("Prompt duplicated — review the draft.");
          setDialogOpen(false);
          reset();
          navigate(`/prompts/${encodeURIComponent(newKey)}/evaluations`);
        } else {
          const detail = (result.data as { detail?: string } | undefined)?.detail;
          notifyError(detail ?? `Failed to duplicate prompt (status ${result.status}).`);
        }
      } catch (err) {
        if (cancelledRef.current) return;
        notifyError(err instanceof Error ? err.message : "Failed to duplicate prompt.");
      }
    },
  });

  useEffect(() => {
    register("organization_id");
    register("report_types");
  }, [register]);

  // Pre-fill name + org + report types from the source every time the dialog opens.
  useEffect(() => {
    if (isDialogOpen) {
      setValue("new_name", `${sourceName} (copy)`);
      setValue("organization_id", sourceOrganizationId ?? GLOBAL_ORG_SENTINEL);
      setValue("report_types", sourceReportTypes);
    }
  }, [isDialogOpen, sourceName, sourceOrganizationId, sourceReportTypes, setValue]);

  useEffect(() => {
    if (!isDialogOpen) {
      reset();
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDebouncedKey("");
    }
  }, [isDialogOpen, reset]);

  const keyValue = watch("new_key") ?? "";
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedKey(KEY_PATTERN.test(keyValue) ? keyValue : "");
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyValue]);
  const keyIsValidFormat = KEY_PATTERN.test(keyValue);

  const { data: dupSearchResp, isFetching: dupCheckPending } = useListPrompts(
    { search: debouncedKey, page_size: 5 },
    { query: { enabled: !!debouncedKey && isDialogOpen } },
  );
  const debounceInSync = keyValue === debouncedKey;
  const keyIsTaken =
    !!debouncedKey &&
    debounceInSync &&
    dupSearchResp?.status === 200 &&
    (dupSearchResp.data.results ?? []).some((p) => p.key === debouncedKey);

  // Routes every dismissal path — Cancel, backdrop click, Escape, the
  // Dialog's own close control — through one place. The Cancel button's
  // onClick used to set cancelledRef directly, but Dialog also calls
  // `setOpen(false)` itself for those other paths, which bypassed it.
  const closeDialog = () => {
    cancelledRef.current = true;
    setDialogOpen(false);
    reset();
  };

  return (
    <Dialog
      title={`Duplicate: ${sourceName}`}
      open={isDialogOpen}
      setOpen={(next) => {
        if (!next) closeDialog();
        else setDialogOpen(next);
      }}
      primaryButton={{
        text: duplicateMutation.isPending ? "Duplicating…" : "Duplicate",
        onClick: onSubmit,
        disabled: duplicateMutation.isPending || formState.isSubmitting || keyIsTaken || RESERVED_KEYS.has(keyValue),
      }}
      secondaryButton={{
        text: "Cancel",
        onClick: closeDialog,
      }}
      size="lg"
    >
      <FormContainer onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-text-secondary">
            Creates a new draft prompt with a copy of <code className="text-text-primary">{sourceKey}</code>&apos;s
            content — role, instructions, inputs, sample output, and response schema. You&apos;ll land on its
            evaluations page to review before opening a PR.
          </p>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-text-secondary">
              New prompt key <span className="text-text-error-primary">*</span>
              <span className="ml-2 text-xs font-normal text-text-tertiary">
                letters and underscores (e.g. <code>{sourceKey}_v2</code>)
              </span>
            </span>
            <FormInput<DuplicatePromptInput>
              register={register}
              formState={formState}
              formKey="new_key"
              required
              placeholder={`${sourceKey}_copy`}
            />
            {keyValue && !keyIsValidFormat && (
              <span className="text-xs text-text-error-primary">
                Must start with a letter and contain only letters, digits, and underscores (2–80 chars).
              </span>
            )}
            {keyIsValidFormat && RESERVED_KEYS.has(keyValue) && (
              <span className="text-xs text-text-error-primary">
                '{keyValue}' is a reserved route — the prompt would be unreachable. Pick a different key.
              </span>
            )}
            {keyIsValidFormat && !RESERVED_KEYS.has(keyValue) && (dupCheckPending || !debounceInSync) && (
              <span className="text-xs text-text-tertiary">Checking availability…</span>
            )}
            {keyIsTaken && (
              <span className="text-xs text-text-error-primary">
                A prompt with that key already exists. Pick a different key.
              </span>
            )}
            {keyIsValidFormat &&
              !RESERVED_KEYS.has(keyValue) &&
              debounceInSync &&
              !dupCheckPending &&
              !keyIsTaken &&
              debouncedKey && <span className="text-xs text-emerald-700">Available.</span>}
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-text-secondary">
              Display name <span className="text-xs font-normal text-text-tertiary">(optional)</span>
            </span>
            <FormInput<DuplicatePromptInput>
              register={register}
              formState={formState}
              formKey="new_name"
              required={false}
              placeholder={`${sourceName} (copy)`}
            />
          </div>

          <OrgOwnerSelect
            orgs={orgs}
            value={watch("organization_id") as string | undefined}
            onChange={(v) => setValue("organization_id", v)}
          />

          <ReportTypeCheckboxes value={watch("report_types") ?? []} onChange={(v) => setValue("report_types", v)} />
        </div>
      </FormContainer>
    </Dialog>
  );
};
